import type { TimeMode } from '../config/world';
import { instantOfWallTime, parseWallTime, wallTimeOf, type WallTime } from '../world/time/zoned';

export interface TimeSample {
  /** World instant, UTC ms. */
  instant: number;
  /** Animation clock, seconds: instant/1000 at speed 1, frozen at speed 0, real-rate when fast-forwarding. */
  animTime: number;
  speed: number;
}

export interface TimeSystemOptions {
  timeZone: string;
  timeMode: TimeMode;
  worldEpoch: string;
  archiveFloor: string;
  viewerTimeZone: string;
  allowBeforeEpoch: boolean;
  start?: WallTime | undefined;
  speed?: number | undefined;
}

function assertSpeed(speed: number): void {
  if (!Number.isFinite(speed) || speed < 0) throw new RangeError(`Invalid time speed: ${speed}`);
}

export class TimeSystem {
  readonly floorInstant: number;
  private following: boolean;
  private speed: number;
  private anchorInstant = 0;
  private anchorReal = 0;
  private offsetMinute = Number.NaN;
  private offsetMs = 0;

  constructor(
    private readonly opts: TimeSystemOptions,
    realNowMs: number,
  ) {
    const floorText = opts.allowBeforeEpoch ? opts.archiveFloor : opts.worldEpoch;
    const floorWall = parseWallTime(floorText);
    if (!floorWall) throw new Error(`Invalid floor date: ${floorText}`);
    this.floorInstant = instantOfWallTime(floorWall, opts.timeZone);
    this.speed = opts.speed ?? 1;
    assertSpeed(this.speed);
    this.following = opts.start === undefined && this.speed === 1;
    const startInstant = opts.start ? instantOfWallTime(opts.start, opts.timeZone) : this.live(realNowMs);
    this.anchor(this.clamp(startInstant, realNowMs), realNowMs);
  }

  sample(realNowMs: number): TimeSample {
    if (this.following) {
      const instant = this.live(realNowMs);
      return { instant, animTime: instant / 1000, speed: this.speed };
    }
    const elapsed = realNowMs - this.anchorReal;
    const instant = this.clamp(this.anchorInstant + elapsed * this.speed, realNowMs);
    const animTime = (this.anchorInstant + (this.speed === 0 ? 0 : elapsed)) / 1000;
    return { instant, animTime, speed: this.speed };
  }

  setSpeed(speed: number, realNowMs: number): void {
    assertSpeed(speed);
    const { instant } = this.sample(realNowMs);
    this.following = false;
    this.speed = speed;
    this.anchor(instant, realNowMs);
  }

  jumpTo(instant: number, realNowMs: number): void {
    this.following = false;
    this.anchor(this.clamp(instant, realNowMs), realNowMs);
  }

  resumeLive(): void {
    this.following = true;
    this.speed = 1;
  }

  private anchor(instant: number, realNowMs: number): void {
    this.anchorInstant = instant;
    this.anchorReal = realNowMs;
  }

  private clamp(instant: number, realNowMs: number): number {
    const cap = Math.max(realNowMs, this.live(realNowMs));
    return Math.min(Math.max(instant, this.floorInstant), cap);
  }

  /** The instant the world shows at real time r with no user offset. */
  private live(realMs: number): number {
    if (this.opts.timeMode === 'world') return realMs;
    const minute = Math.floor(realMs / 60_000);
    if (minute !== this.offsetMinute) {
      const minuteStart = minute * 60_000;
      const wall = wallTimeOf(minuteStart, this.opts.viewerTimeZone);
      this.offsetMs = instantOfWallTime(wall, this.opts.timeZone) - minuteStart;
      this.offsetMinute = minute;
    }
    return realMs + this.offsetMs;
  }
}
